import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { format, parseISO, differenceInDays } from 'date-fns';
import { aISO, fmtFecha } from '../utils/dates';
import { mismaPersona } from '../utils/nameMatch';
import { pedirJSONIA } from '../utils/aiClient';
import { companiaCanonica } from '../utils/companies';
import { Plus, X, CheckCircle, XCircle, Upload, Sparkles, Loader, MessageCircle, Phone, Send, Trash2 } from 'lucide-react';
import * as XLSX from 'xlsx';

const MOTIVOS = ['Falta de pago de cuota','Cheque rechazado','Débito rechazado','Tarjeta vencida','Otro'];

// ── Parser universal de archivos de deudores ──────────────
// Formatea una fecha sin tirar nunca: si no es ISO válida, devuelve '—'.
// (date-fns format() lanza RangeError con fechas inválidas y eso, en pleno
// render, desmonta toda la app. Este guard evita el crash.)
// Delega en el módulo central de fechas (robusto, nunca lanza).
const fmtFechaSegura = (iso) => fmtFecha(iso);

// Parsea un monto en formato uruguayo (punto = miles, coma = decimales) a un
// string numérico con punto decimal. Resuelve los casos típicos de los listados
// de las compañías: "444,74" (coma decimal), "2.967,19" (miles + decimal),
// "44.474" / "12.500" (punto de miles). Pista: decimales son 2, miles de a 3.
const parseMontoUY = (raw) => {
  let s = String(raw || '').replace(/[^0-9.,]/g, '').trim();
  if (!s) return '';
  const tieneComa = s.includes(',');
  const tienePunto = s.includes('.');
  if (tieneComa && tienePunto) {
    s = s.replace(/\./g, '').replace(',', '.');          // miles + decimal
  } else if (tieneComa) {
    s = s.replace(',', '.');                              // solo coma = decimal
  } else if (tienePunto) {
    const partes = s.split('.');
    const ultimo = partes[partes.length - 1];
    s = ultimo.length === 3
      ? partes.join('')                                  // "44.474" -> 44474
      : partes.slice(0, -1).join('') + '.' + ultimo;     // "444.74" -> 444.74
  }
  return s;
};

function parseDebtorFile(data) {
  let headerRow = -1, headers = [];
  for (let i = 0; i < Math.min(10, data.length); i++) {
    if (!Array.isArray(data[i])) continue;
    const row = data[i].map(c => String(c||'').toLowerCase().trim());
    if (row.some(c => c.includes('asegurado')||c.includes('poliza')||c.includes('póliza')||c.includes('deuda')||c.includes('importe')||c.includes('nombre'))) {
      headerRow = i;
      headers = data[i].map(c => String(c||'').toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g,''));
      break;
    }
  }
  if (headerRow === -1) return [];

  const get = (row, ...keys) => {
    // Para cada clave, primero buscamos coincidencia EXACTA del encabezado y solo
    // si no hay, parcial. Esto evita que "TIPO DOCUMENTO ASEGURADO" gane sobre
    // "ASEGURADO" (ambas contienen "asegurado"): la exacta tiene prioridad.
    for (const k of keys) {
      const idxExacto = headers.findIndex(h => h === k);
      if (idxExacto !== -1 && row[idxExacto] !== undefined && String(row[idxExacto]).trim() !== '') {
        return String(row[idxExacto]).trim();
      }
    }
    for (const k of keys) {
      const idx = headers.findIndex(h => h.includes(k));
      if (idx !== -1 && row[idx] !== undefined && String(row[idx]).trim() !== '') return String(row[idx]).trim();
    }
    return '';
  };

  // Normaliza a ISO 'yyyy-MM-dd' usando el módulo central (cubre serial Excel,
  // dd/MM/yyyy, yyyy/MM/dd, etc.). Devuelve '' si no es interpretable.
  const parseDate = (val) => aISO(val);
  const parseMonto = parseMontoUY;

  const results = [];
  for (let i = headerRow + 1; i < data.length; i++) {
    const row = data[i];
    if (!Array.isArray(row) || row.every(c => !c)) continue;
    const clientName = get(row, 'asegurado', 'nombre asegurado', 'nombre', 'cliente');
    const policyNumber = get(row, 'poliza', 'nro poliza', 'numero poliza');
    const amount = get(row, 'deuda', 'importe cuota', 'importe', 'monto', 'cuota');
    const dueDate = parseDate(get(row, 'vencimiento', 'fecha venc', 'fecha cancelacion'));
    const currencyRaw = get(row, 'moneda');
    const currency = (currencyRaw.includes('U$S')||currencyRaw==='USD'||currencyRaw==='u$s') ? 'USD' : 'UYU';
    const phone = get(row, 'telefono movil', 'telefono', 'celular', 'movil');
    const dni = get(row, 'documento asegurado', 'documento', 'rut', 'ci ', 'cedula');
    const ramo = get(row, 'riesgo', 'ramo');
    const cuota = get(row, 'nro cuota', 'n? cuota', 'numero cuota');
    // OJO: no usar claves cortas tipo 'cia' — la búsqueda parcial matchea
    // "PROVINCIA"/"AGENCIA" y termina tomando el departamento como compañía.
    // Además se valida contra la lista canónica: si no es una aseguradora
    // conocida (Sura, BSE, Berkley…), se guarda vacío.
    const companyRaw = get(row, 'compania', 'compañia', 'compañía', 'aseguradora');
    const company = companiaCanonica(companyRaw) || '';
    if (!clientName && !policyNumber) continue;
    results.push({
      clientName: clientName.replace(/\s+/g,' '),
      policyNumber: policyNumber.replace(/'/g,'').trim(),
      amount: parseMonto(amount),
      currency, dueDate: dueDate || format(new Date(),'yyyy-MM-dd'),
      phone, dni, motivo: 'Falta de pago de cuota', cuotas: cuota,
      company: company.trim(),
      notes: ramo ? `Ramo: ${ramo}` : '',
    });
  }
  return results;
}

// ── Modal importar archivo ────────────────────────────────
function ImportDebtorsModal({ onClose }) {
  const { clients, policies, debts, addDebt, showToast } = useApp();
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const norm = s => String(s||'').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/\s+/g,' ').trim();

  // Duplicado REAL: mismo cliente Y misma deuda concreta (mismo importe y
  // vencimiento; si ambos lados tienen póliza o cuota, también deben coincidir).
  // Antes alcanzaba con que el cliente tuviera CUALQUIER mora pendiente, lo
  // que hacía omitir avisos nuevos legítimos (otra cuota, otra póliza).
  const isDupe = (r) => debts.some(d => {
    if (d.status !== 'pending') return false;
    const c = clients.find(cl => cl.id === d.clientId);
    if (!c || norm(c.name) !== norm(r.clientName)) return false;
    const numEq = (a, b) => Math.abs((parseFloat(a) || 0) - (parseFloat(b) || 0)) < 0.01;
    if (!numEq(d.amount, r.amount)) return false;
    if (d.dueDate && r.dueDate && d.dueDate !== r.dueDate) return false;
    if (d.cuotas && r.cuotas && String(d.cuotas).trim() !== String(r.cuotas).trim()) return false;
    return true;
  });

  const findClient = (r) => {
    // Normaliza identificadores; descarta valores basura (vacíos o muy cortos)
    // que harían matchear a cualquiera (ej. dni "0", teléfono "-").
    const dniR = (r.dni || '').replace(/[\s.]/g,'');
    const phoneR = (r.phone || '').replace(/\s/g,'');
    const dniOk = dniR.length >= 6;        // una CI/RUT real tiene al menos 6-7 dígitos
    const phoneOk = phoneR.length >= 7;    // un teléfono real tiene al menos 7 dígitos
    const nombreCompatible = (c) => !r.clientName || mismaPersona(c.name, r.clientName);

    // DNI: match fuerte, PERO exigimos que el nombre sea compatible. Un DNI
    // compartido o mal cargado no debe asignar la deuda a otra persona.
    if (dniOk) {
      const c = clients.find(c => (c.dni || '').replace(/[\s.]/g,'') === dniR && nombreCompatible(c));
      if (c) return c;
    }
    // Teléfono: mismo criterio. Es el más propenso a falsos positivos (familias,
    // teléfono de la corredora repetido), así que el nombre DEBE coincidir.
    if (phoneOk) {
      const c = clients.find(c => (c.phone || '').replace(/\s/g,'') === phoneR && nombreCompatible(c));
      if (c) return c;
    }
    // Nombre: coincidencia exacta normalizada (criterio seguro de siempre).
    const porNombre = clients.find(c => norm(c.name) === norm(r.clientName));
    if (porNombre) return porNombre;
    // Último recurso: persona compatible por nombre (cubre orden invertido,
    // segundo nombre, etc.) usando el matcheo robusto. Sin random: exige
    // solapamiento alto de palabras, no una sola en común.
    if (r.clientName) {
      const candidatos = clients.filter(c => mismaPersona(c.name, r.clientName));
      // Solo si es inequívoco: un único candidato. Si hay varios, no adivinamos.
      if (candidatos.length === 1) return candidatos[0];
    }
    return null;
  };

  const findPolicy = (r, clientId) => {
    if (!r.policyNumber || !clientId) return null;
    return policies.find(p => p.clientId === clientId &&
      p.number?.replace(/'/g,'').trim().toLowerCase() === r.policyNumber.toLowerCase());
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError(''); setLoading(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        // IMPORTANTE: raw:false hace que XLSX devuelva el TEXTO de cada celda
        // tal como está en el archivo, sin convertir números. Sin esto, XLSX
        // interpreta "444,74" (coma decimal uruguaya) como separador de miles y
        // lo convierte al número 44474, corrompiendo el monto antes de parsearlo.
        const wb = XLSX.read(ev.target.result, { type: 'binary', cellDates: false, cellText: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false });
        const parsed = parseDebtorFile(raw);
        if (parsed.length === 0) {
          setError('No se encontraron registros. El archivo debe tener columnas como: Asegurado, Póliza, Deuda/Importe, Vencimiento.');
          setLoading(false); return;
        }
        const enriched = parsed.map(r => ({
          ...r,
          matchedClient: findClient(r),
          matchedPolicy: null, // se resuelve abajo
          isDupe: isDupe(r),
        })).map(r => ({
          ...r,
          matchedPolicy: r.matchedClient ? findPolicy(r, r.matchedClient.id) : null,
        }));
        setPreview(enriched);
        const sel = {};
        enriched.forEach((r, i) => { sel[i] = !r.isDupe; });
        setSelected(sel);
      } catch(err) { setError('Error al leer: ' + err.message); }
      setLoading(false);
    };
    reader.onerror = () => { setError('No se pudo leer el archivo. Probá de nuevo.'); setLoading(false); };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  const handleImport = async () => {
    let created = 0;
    for (const [i, r] of preview.entries()) {
      if (!selected[i]) continue; // el "posible duplicado" solo desmarca por defecto; si lo marcás, se importa
      await addDebt({
        clientId: r.matchedClient?.id || '',
        policyId: r.matchedPolicy?.id || '',
        amount: r.amount, currency: r.currency, dueDate: r.dueDate,
        motivo: r.motivo, cuotas: r.cuotas,
        company: r.company || r.matchedPolicy?.company || '',
        notes: [r.notes, !r.matchedClient ? `Asegurado: ${r.clientName}` : '', !r.matchedPolicy && r.policyNumber ? `Póliza: ${r.policyNumber}` : ''].filter(Boolean).join(' · '),
        status: 'pending',
      });
      created++;
    }
    showToast(`✅ ${created} mora${created!==1?'s':''} importada${created!==1?'s':''}`);
    onClose();
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:860 }}>
        <div className="modal-header">
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <Upload size={18} color="var(--accent)" />
            <h2 style={{ fontFamily:'var(--font-display)', fontSize:17, fontWeight:700 }}>Importar lista de deudores</h2>
          </div>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding:'6px 8px' }}><X size={14} /></button>
        </div>
        <div className="modal-body">
          <div style={{ background:'var(--bg-input)', borderRadius:8, padding:'10px 14px', fontSize:12, color:'var(--text-secondary)', marginBottom:12 }}>
            Compatible con <strong>Sura</strong> (XLS — "Clientes con cuotas no pagadas"), <strong>Berkley</strong> (CSV — "Lista deudores"), <strong>BSE</strong> y otras compañías. Vincula automáticamente con clientes y pólizas del sistema.
          </div>
          {!preview ? (
            <>
              <label style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:10, cursor:'pointer', border:'2px dashed var(--border)', borderRadius:12, padding:36, background:'var(--bg-input)' }}>
                <Upload size={32} color="var(--accent)" />
                <span style={{ fontSize:14, fontWeight:600 }}>Clic para seleccionar el archivo</span>
                <span style={{ fontSize:12, color:'var(--text-muted)' }}>.xls · .xlsx · .csv</span>
                <input type="file" onClick={e => { e.target.value = null; }} accept=".xls,.xlsx,.csv" onChange={handleFile} style={{ display:'none' }} />
              </label>
              {loading && <p style={{ textAlign:'center', fontSize:13, color:'var(--accent)', marginTop:12 }}>Leyendo archivo...</p>}
              {error && <div style={{ background:'var(--red-dim)', border:'1px solid rgba(255,77,109,0.2)', borderRadius:8, padding:'10px 14px', fontSize:13, color:'var(--red)', marginTop:10 }}>{error}</div>}
            </>
          ) : (
            <>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <span style={{ fontSize:13, fontWeight:600, color:'var(--accent)' }}>
                  {preview.length} registro{preview.length!==1?'s':''} · {preview.filter(r=>r.matchedClient).length} vinculados · {preview.filter(r=>r.isDupe).length} posibles duplicados
                </span>
                <button className="btn btn-secondary btn-sm" onClick={()=>{setPreview(null);setSelected({});}}>← Otro archivo</button>
              </div>
              <div style={{ maxHeight:380, overflowY:'auto', overflowX:'auto', WebkitOverflowScrolling:'touch', border:'1px solid var(--border)', borderRadius:10 }}>
                <table style={{ width:'100%', minWidth:640, borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:'var(--bg-secondary)', position:'sticky', top:0 }}>
                      <th style={{ width:36, padding:'8px 12px' }}></th>
                      {['Asegurado','Póliza','Monto','Vence','Cliente vinculado','Póliza vinculada','Estado'].map(h=>(
                        <th key={h} style={{ textAlign:'left', padding:'8px 12px', fontSize:11, color:'var(--text-muted)', fontFamily:'var(--font-display)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((r,i) => (
                      <tr key={i} style={{ borderTop:'1px solid var(--border)', background:selected[i]?'rgba(124,108,248,0.03)':'transparent', opacity:r.isDupe&&!selected[i]?0.55:1, cursor:'pointer' }}
                        onClick={()=>setSelected(s=>({...s,[i]:!s[i]}))}>
                        <td style={{ padding:'8px 12px' }}>
                          <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${selected[i]?'var(--accent)':'var(--border)'}`, background:selected[i]?'var(--accent)':'transparent', display:'flex', alignItems:'center', justifyContent:'center' }}>
                            {selected[i]&&<CheckCircle size={10} color="#fff" />}
                          </div>
                        </td>
                        <td style={{ padding:'8px 12px', fontSize:13, fontWeight:500 }}>{r.clientName}</td>
                        <td style={{ padding:'8px 12px', fontSize:12, fontFamily:'monospace', color:'var(--text-secondary)' }}>{r.policyNumber||'—'}</td>
                        <td style={{ padding:'8px 12px', fontSize:13, fontWeight:700, color:'var(--red)' }}>{r.currency==='USD'?'U$S':'$'} {r.amount}</td>
                        <td style={{ padding:'8px 12px', fontSize:12 }}>{fmtFechaSegura(r.dueDate)}</td>
                        <td style={{ padding:'8px 12px', fontSize:12 }}>{r.matchedClient?<span style={{ color:'var(--accent)' }}>✓ {r.matchedClient.name}</span>:<span style={{ color:'var(--amber)' }}>⚠ Sin vincular</span>}</td>
                        <td style={{ padding:'8px 12px', fontSize:12 }}>{r.matchedPolicy?<span style={{ color:'var(--accent)' }}>✓ {r.matchedPolicy.number}</span>:<span style={{ color:'var(--text-muted)' }}>—</span>}</td>
                        <td style={{ padding:'8px 12px' }}>{r.isDupe?<span className="badge badge-gray" style={{ fontSize:10 }}>Posible duplicado</span>:<span className="badge badge-green" style={{ fontSize:10 }}>Nueva</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">Cancelar</button>
          {preview && <button onClick={handleImport} disabled={selectedCount===0} className="btn btn-primary">
            <Upload size={14} /> Importar {selectedCount} mora{selectedCount!==1?'s':''}
          </button>}
        </div>
      </div>
    </div>
  );
}

function DebtModal({ debt, onClose }) {
  const { addDebt, updateDebt, clients, policies, users } = useApp();
  const isEdit = !!(debt && debt.id);

  // Inicialización inteligente: recuperar el nombre (puede estar en notes de moras viejas)
  // y autovincular si ya existe un cliente con ese nombre.
  const initForm = () => {
    const base = debt || {
      clientId:'', clientName:'', policyId:'', amount:'', currency:'UYU',
      dueDate: format(new Date(),'yyyy-MM-dd'),
      motivo:'Falta de pago de cuota', cuotas:'', notes:'', emailRef:'',
      assignedToId:'', assignedTo:'',
    };
    let f = { ...base };
    // Si no hay clientName pero está escondido en notas, recuperarlo
    if (!f.clientName && f.notes) {
      const m = f.notes.match(/(?:Cliente sin ficha|Asegurado):\s*([^·]+)/i);
      if (m) f.clientName = m[1].trim();
    }
    // Si no está vinculado pero el nombre coincide con un cliente existente, sugerir el vínculo
    if (!f.clientId && f.clientName) {
      const match = clients.find(c => c.name?.toLowerCase().trim() === f.clientName.toLowerCase().trim());
      if (match) f.clientId = match.id;
    }
    return f;
  };
  const [form, setForm] = useState(initForm);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const sortedClients = [...clients].sort((a,b)=>a.name?.localeCompare(b.name)||0);
  const sortedUsers = [...(users||[])].sort((a,b)=>a.name?.localeCompare(b.name)||0);
  const clientPolicies = policies.filter(p=>p.clientId===form.clientId);

  // Cliente del sistema cuyo nombre coincide (para sugerir vínculo)
  const sugerido = !form.clientId && form.clientName
    ? clients.find(c => c.name?.toLowerCase().trim() === form.clientName.toLowerCase().trim())
    : null;

  // El usuario escribe el nombre; si coincide con un cliente, lo vinculamos
  const onClientText = (val) => {
    const match = clients.find(c => c.name?.toLowerCase().trim() === val.toLowerCase().trim());
    setForm(f => ({ ...f, clientName: val, clientId: match ? match.id : '', policyId: '' }));
  };
  const clienteVinculado = !!form.clientId;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.clientName?.trim() && !form.clientId) { alert('Ingresá el nombre del cliente.'); return; }
    const payload = { ...form };
    // Limpiar de notas la marca vieja de "Cliente sin ficha" si ahora está vinculado
    if (form.clientId && payload.notes) {
      payload.notes = payload.notes.replace(/(?:^|·\s*)Cliente sin ficha:[^·]*/i, '').replace(/^·\s*/, '').trim();
    }
    // Si no hay cliente vinculado, dejamos el nombre en notes para no perderlo
    if (!form.clientId && form.clientName && !/Cliente sin ficha/i.test(payload.notes || '')) {
      payload.notes = [payload.notes, `Cliente sin ficha: ${form.clientName}`].filter(Boolean).join(' · ');
    }
    if (isEdit) await updateDebt(debt.id, payload);
    else await addDebt({ ...payload, status:'pending' });
    onClose();
  };
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:560 }}>
        <div className="modal-header">
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:17, fontWeight:700 }}>{isEdit?'Editar mora':'Registrar mora'}</h2>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding:'6px 8px' }}><X size={14} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Cliente *</label>
              <input value={form.clientName} onChange={e=>onClientText(e.target.value)} list="debt-clients-list" required placeholder="Nombre del cliente (escribilo aunque no esté cargado)" autoComplete="off" />
              <datalist id="debt-clients-list">
                {sortedClients.map(c=><option key={c.id} value={c.name} />)}
              </datalist>
              {clienteVinculado ? (
                <p style={{ fontSize:11, color:'var(--accent)', marginTop:4 }}>✓ Vinculado a un cliente existente</p>
              ) : sugerido ? (
                <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, padding:'6px 10px', background:'var(--accent-dim)', borderRadius:6 }}>
                  <span style={{ fontSize:11.5, color:'var(--text)' }}>Encontramos un cliente que coincide: <strong>{sugerido.name}</strong></span>
                  <button type="button" className="btn btn-primary btn-sm" style={{ padding:'2px 10px', marginLeft:'auto' }}
                    onClick={()=>setForm(f=>({...f, clientId:sugerido.id, clientName:sugerido.name, policyId:''}))}>Vincular</button>
                </div>
              ) : (
                <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>Cliente sin ficha — se guardará igual. Si ya lo cargaste, escribí el nombre exacto para vincularlo.</p>
              )}
            </div>
            {form.clientId && (
              <div className="form-group">
                <label className="form-label">Póliza asociada</label>
                <select value={form.policyId} onChange={e=>{
                  const pid = e.target.value;
                  const pol = clientPolicies.find(p=>p.id===pid);
                  setForm(f=>({...f, policyId:pid, company: pol?.company || f.company || ''}));
                }}>
                  <option value="">Sin póliza específica</option>
                  {clientPolicies.map(p=><option key={p.id} value={p.id}>{p.number} — {p.company}</option>)}
                </select>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Compañía</label>
              <input value={form.company||''} onChange={e=>set('company',e.target.value)} list="debt-companies-list" placeholder="Ej: Sura, BSE, Berkley…" autoComplete="off" />
              <datalist id="debt-companies-list">
                {[...new Set(policies.map(p=>p.company).filter(Boolean))].sort().map(c=><option key={c} value={c} />)}
              </datalist>
            </div>
            <div className="form-grid">
              <div className="form-group"><label className="form-label">Monto *</label><input type="number" value={form.amount} onChange={e=>set('amount',e.target.value)} required placeholder="0" /></div>
              <div className="form-group"><label className="form-label">Moneda</label><select value={form.currency} onChange={e=>set('currency',e.target.value)}><option value="UYU">$ UYU</option><option value="USD">U$S</option></select></div>
              <div className="form-group"><label className="form-label">Vencimiento *</label><input type="date" value={form.dueDate} onChange={e=>set('dueDate',e.target.value)} required /></div>
              <div className="form-group"><label className="form-label">Cuota(s)</label><input value={form.cuotas} onChange={e=>set('cuotas',e.target.value)} placeholder="Ej: 3/12" /></div>
            </div>
            <div className="form-group">
              <label className="form-label">Motivo</label>
              <select value={form.motivo} onChange={e=>set('motivo',e.target.value)}>
                {MOTIVOS.map(m=><option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Asignar a</label>
              <select value={form.assignedToId||''} onChange={e=>{
                const u = sortedUsers.find(u=>u.id===e.target.value);
                setForm(f=>({...f, assignedToId:e.target.value, assignedTo:u?.name||''}));
              }}>
                <option value="">Sin asignar</option>
                {sortedUsers.map(u=><option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
              <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>Si asignás a alguien, recibe un email con enlace directo a la deuda.</p>
            </div>
            <div className="form-group"><label className="form-label">Referencia email/aviso</label><input value={form.emailRef} onChange={e=>set('emailRef',e.target.value)} placeholder="Asunto del mail recibido" /></div>
            <div className="form-group"><label className="form-label">Notas internas</label><textarea value={form.notes} onChange={e=>set('notes',e.target.value)} rows={2} /></div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancelar</button>
            <button type="submit" className="btn btn-primary">{isEdit?'Guardar':'Registrar mora'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal: pegar mail/texto y extraer con IA ───
function DebtIAModal({ onClose, onDone }) {
  const { clients, addDebt } = useApp();
  const [texto, setTexto] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detectados, setDetectados] = useState(null); // array de moras detectadas
  const [selected, setSelected] = useState({});
  const [guardando, setGuardando] = useState(false);

  const procesar = async () => {
    if (!texto.trim()) { setError('Pegá el texto del mail o aviso.'); return; }
    setError(''); setLoading(true);
    try {
      const arr = await pedirJSONIA({
        esperado: 'array',
        maxTokens: 2000,
        content: `Este es un aviso (mail o mensaje) de una compañía de seguros informando una o varias deudas/moras. PUEDE HABER VARIOS DEUDORES en el mismo texto. Extraé TODOS. Respondé ÚNICAMENTE con un JSON válido, sin texto ni backticks, con un ARRAY de objetos (uno por cada deudor), con esta estructura:
[{"clientName":"","amount":"","currency":"UYU","dueDate":"","cuotas":"","motivo":"","policyNumber":"","company":"","emailRef":""}]

Instrucciones:
- Devolvé SIEMPRE un array, aunque haya un solo deudor (un array de un elemento).
- Incluí una entrada por CADA deudor/mora mencionado en el texto. Si hay 2 morosos, devolvé 2 objetos; si hay 5, devolvé 5.
- clientName: nombre del asegurado/cliente deudor
- amount: monto adeudado, solo número sin símbolos ni puntos de miles (ej "12500")
- currency: "UYU" si es en pesos, "USD" si es en dólares
- dueDate: fecha de vencimiento en formato YYYY-MM-DD. Si no hay, ""
- cuotas: número de cuota si se menciona (ej "3/12"), si no ""
- motivo: uno de: "Falta de pago de cuota", "Cheque rechazado", "Débito rechazado", "Tarjeta vencida", "Otro"
- policyNumber: número de póliza si aparece, si no ""
- company: la compañía aseguradora que emite el aviso (ej "Sura", "BSE", "Berkley", "Mapfre", "Porto Seguro", "Sancor"). Suele estar en el encabezado, logo, firma o asunto del mail. Es la misma para todos los deudores del mismo aviso. Si no se identifica, "".
- emailRef: asunto o referencia corta del aviso
Extraé solo lo que veas, sin inventar. Si un dato no está, dejalo "".

Texto del aviso:
${texto}`,
      });
      if (!Array.isArray(arr) || arr.length === 0) throw new Error('No se detectaron deudores en el texto.');
      const procesadas = arr.map(p => {
        const match = clients.find(c => c.name?.toLowerCase().trim() === (p.clientName || '').toLowerCase().trim());
        return {
          clientId: match ? match.id : '',
          clientName: p.clientName || '',
          matched: !!match,
          policyId: '',
          amount: parseMontoUY(p.amount) || '',
          currency: p.currency === 'USD' ? 'USD' : 'UYU',
          dueDate: p.dueDate || format(new Date(), 'yyyy-MM-dd'),
          motivo: p.motivo || 'Falta de pago de cuota',
          cuotas: p.cuotas || '',
          company: p.company || '',
          emailRef: p.emailRef || '',
          notes: [p.policyNumber ? `Póliza: ${p.policyNumber}` : ''].filter(Boolean).join(' · '),
        };
      });
      setDetectados(procesadas);
      // todas seleccionadas por defecto
      const sel = {}; procesadas.forEach((_, i) => { sel[i] = true; });
      setSelected(sel);
      setLoading(false);
    } catch (err) {
      setError('Error al procesar: ' + err.message);
      setLoading(false);
    }
  };

  const guardar = async () => {
    setGuardando(true);
    let n = 0;
    for (let i = 0; i < detectados.length; i++) {
      if (!selected[i]) continue;
      const { matched, ...rest } = detectados[i];
      const payload = { ...rest, status: 'pending' };
      if (!rest.clientId && rest.clientName) {
        payload.notes = [rest.notes, `Cliente sin ficha: ${rest.clientName}`].filter(Boolean).join(' · ');
      }
      await addDebt(payload);
      n++;
    }
    setGuardando(false);
    onDone(n);
  };

  const selCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:640 }}>
        <div className="modal-header">
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:17, fontWeight:700 }}>Cargar moras desde mail/texto</h2>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding:'6px 8px' }}><X size={14} /></button>
        </div>
        <div className="modal-body">
          {!detectados ? (
            <>
              <p style={{ fontSize:12.5, color:'var(--text-muted)', marginBottom:12 }}>Pegá el mail o aviso que te mandó la compañía. La IA detecta a todos los deudores del texto (pueden ser varios) y los muestra para que revises antes de guardar.</p>
              <textarea value={texto} onChange={e=>setTexto(e.target.value)} rows={9} placeholder="Pegá acá el texto del aviso de deuda..." style={{ width:'100%', resize:'vertical' }} />
              {error && <div style={{ fontSize:12, color:'var(--red)', marginTop:8 }}>{error}</div>}
            </>
          ) : (
            <>
              <p style={{ fontSize:12.5, color:'var(--text-muted)', marginBottom:12 }}>Se detectaron <strong>{detectados.length}</strong> deudor{detectados.length!==1?'es':''}. Revisá y destildá los que no quieras registrar.</p>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {detectados.map((d, i) => (
                  <div key={i} onClick={()=>setSelected(s=>({...s, [i]:!s[i]}))}
                    style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 12px', background:'var(--bg-input)', borderRadius:8, cursor:'pointer', opacity:selected[i]?1:0.5 }}>
                    <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${selected[i]?'var(--accent)':'var(--border)'}`, background:selected[i]?'var(--accent)':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      {selected[i]&&<CheckCircle size={11} color="#fff" />}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:13.5 }}>{d.clientName || '(sin nombre)'}
                        {d.matched ? <span style={{ fontSize:10, color:'var(--accent)', marginLeft:8 }}>✓ cliente vinculado</span> : <span style={{ fontSize:10, color:'var(--text-muted)', marginLeft:8 }}>sin ficha</span>}
                      </div>
                      <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>
                        {d.company ? <span style={{ color:'var(--accent)', fontWeight:500 }}>{d.company} · </span> : ''}{d.currency==='USD'?'U$S':'$'} {Number(d.amount||0).toLocaleString('es-UY')} · vence {d.dueDate||'—'}{d.cuotas?` · cuota ${d.cuotas}`:''} · {d.motivo}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {error && <div style={{ fontSize:12, color:'var(--red)', marginTop:8 }}>{error}</div>}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn btn-secondary">Cancelar</button>
          {!detectados ? (
            <button type="button" onClick={procesar} disabled={loading} className="btn btn-primary">
              {loading ? <><Loader size={14} style={{ animation:'spin 1s linear infinite' }} /> Procesando...</> : <><Sparkles size={14} /> Detectar deudores</>}
            </button>
          ) : (
            <>
              <button type="button" onClick={()=>{setDetectados(null);setError('');}} className="btn btn-secondary">← Volver</button>
              <button type="button" onClick={guardar} disabled={guardando||selCount===0} className="btn btn-primary">
                {guardando ? 'Guardando...' : `Registrar ${selCount} mora${selCount!==1?'s':''}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Recordatorios WhatsApp a deudores ───
// La IA redacta un mensaje personalizado por cada deudor con teléfono.
// El usuario revisa, edita si quiere, y abre WhatsApp con un clic (wa.me).
// No se envía nada automáticamente: el disparo siempre es manual.
function WhatsAppModal({ onClose }) {
  const { debts, clients, policies, updateDebt, showToast } = useApp();
  const today = new Date().toISOString().split('T')[0];

  const getClient = id => clients.find(c => c.id === id) || {};
  const getPolicy = id => policies.find(p => p.id === id) || {};

  // Normaliza un teléfono uruguayo a formato internacional para wa.me (sin +, sin espacios)
  // UY: código país 598. Celulares locales suelen ser 09XXXXXXX (9 dígitos) → 5989XXXXXXX
  const normalizePhone = (raw) => {
    if (!raw) return '';
    let p = String(raw).replace(/[^\d]/g, '');
    if (!p) return '';
    if (p.startsWith('598')) return p;
    // 09XXXXXXX → quitar el 0 inicial y anteponer 598
    if (p.startsWith('0')) p = p.slice(1);
    return '598' + p;
  };

  // Deudores pendientes, con el teléfono resuelto (del cliente vinculado)
  const pendientes = debts
    .filter(d => d.status === 'pending')
    .map(d => {
      const client = getClient(d.clientId);
      const policy = getPolicy(d.policyId);
      let nombre = client.name || d.clientName || '';
      if (!nombre && d.notes) {
        const m = d.notes.match(/(?:Cliente sin ficha|Asegurado):\s*([^·]+)/i);
        if (m) nombre = m[1].trim();
      }
      const phoneRaw = client.phone || d.phone || '';
      return {
        debt: d,
        nombre: nombre || '(sin nombre)',
        phoneRaw,
        phone: normalizePhone(phoneRaw),
        policyNumber: policy.number || '',
        company: policy.company || '',
        isOverdue: d.dueDate < today,
      };
    })
    .filter(x => x.phone); // solo los que tienen teléfono utilizable

  const sinTelefono = debts.filter(d => d.status === 'pending').length - pendientes.length;

  const [mensajes, setMensajes] = useState({}); // debtId -> texto del mensaje
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generado, setGenerado] = useState(false);
  const [apiConfigured, setApiConfigured] = useState(false);
  const [enviando, setEnviando] = useState({}); // debtId -> bool
  const [enviadoApi, setEnviadoApi] = useState({}); // debtId -> 'sent' | 'failed'

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    ...(localStorage.getItem('nebb_token') ? { Authorization: `Bearer ${localStorage.getItem('nebb_token')}` } : {}),
  });

  // Al abrir, consultamos si la WhatsApp Business API está configurada en el server.
  React.useEffect(() => {
    fetch('/api/whatsapp/status', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setApiConfigured(!!d.configured))
      .catch(() => setApiConfigured(false));
  }, []);

  // Pide a la IA un mensaje personalizado por cada deudor (en un solo request)
  const generarMensajes = async () => {
    if (pendientes.length === 0) { setError('No hay deudores pendientes con teléfono.'); return; }
    setError(''); setLoading(true);
    try {
      const lista = pendientes.map((x, i) => (
        `${i}|${x.nombre}|${x.debt.currency === 'USD' ? 'U$S' : '$'} ${Number(x.debt.amount || 0).toLocaleString('es-UY')}|póliza ${x.policyNumber || 's/n'}${x.company ? ' (' + x.company + ')' : ''}|vence ${x.debt.dueDate || 's/f'}|${x.isOverdue ? 'YA VENCIDA' : 'por vencer'}`
      )).join('\n');

      const prompt = `Sos asistente de NEBB Brokers, una corredora de seguros uruguaya. Redactá un mensaje de WhatsApp CORTO, cordial y profesional para cada cliente con una cuota de seguro impaga, recordándole amablemente el pago para que no se le caiga la cobertura.

Pautas:
- Tono uruguayo, cercano pero profesional. Tratá de "usted" no, usá tuteo cordial ("Hola Juan, ¿cómo estás?").
- Mencioná el monto, la póliza/compañía y la fecha de vencimiento.
- Si está vencida, pedí regularizar a la brevedad para no perder la cobertura. Si está por vencer, es un recordatorio preventivo.
- Cerrá ofreciendo ayuda y firmando como "NEBB Brokers".
- Máximo 4-5 líneas. Sin emojis excesivos (uno o ninguno).
- NO inventes datos que no estén en la lista.

Respondé ÚNICAMENTE con un JSON válido (sin backticks ni texto extra): un array de objetos {"i": <índice>, "mensaje": "<texto>"}, uno por cada cliente de la lista.

Lista de deudores (índice|nombre|monto|póliza|vencimiento|estado):
${lista}`;

      const arr = await pedirJSONIA({ content: prompt, maxTokens: 4000, esperado: 'array' });
      const map = {};
      arr.forEach(item => {
        const x = pendientes[item.i];
        if (x) map[x.debt.id] = item.mensaje;
      });
      setMensajes(map);
      setGenerado(true);
      setLoading(false);
    } catch (err) {
      setError('Error al generar mensajes: ' + err.message);
      setLoading(false);
    }
  };

  const abrirWhatsApp = (x) => {
    const texto = mensajes[x.debt.id] || '';
    const url = `https://wa.me/${x.phone}?text=${encodeURIComponent(texto)}`;
    window.open(url, '_blank');
    // Marcar como contactado (no bloquea la UI si falla)
    updateDebt(x.debt.id, { whatsappSentAt: new Date().toISOString() });
    showToast(`📲 WhatsApp abierto para ${x.nombre}`);
  };

  // Envío automático vía WhatsApp Business API (solo si está configurada).
  // Usa una plantilla aprobada por Meta. El mensaje libre de la IA NO se puede
  // enviar como business-initiated; Meta exige plantilla. Por eso mandamos los
  // datos como parámetros de la plantilla 'recordatorio_deuda'.
  const enviarApi = async (x) => {
    setEnviando(s => ({ ...s, [x.debt.id]: true }));
    try {
      // Parámetros de la plantilla, en orden {{1}}..{{4}}
      const bodyParams = [
        x.nombre,
        `${x.debt.currency === 'USD' ? 'U$S' : '$'} ${Number(x.debt.amount || 0).toLocaleString('es-UY')}`,
        x.policyNumber || 's/n',
        x.debt.dueDate || 's/f',
      ];
      const res = await fetch('/api/whatsapp/send', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          debtId: x.debt.id,
          to: x.phone,
          clientName: x.nombre,
          templateName: 'recordatorio_deuda',
          languageCode: 'es',
          bodyParams,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setEnviadoApi(s => ({ ...s, [x.debt.id]: 'sent' }));
        updateDebt(x.debt.id, { whatsappSentAt: new Date().toISOString() });
        showToast(`✅ Recordatorio enviado a ${x.nombre}`);
      } else {
        setEnviadoApi(s => ({ ...s, [x.debt.id]: 'failed' }));
        showToast(`❌ No se pudo enviar a ${x.nombre}: ${data.error || 'error'}`, 'warning');
      }
    } catch (err) {
      setEnviadoApi(s => ({ ...s, [x.debt.id]: 'failed' }));
      showToast(`❌ Error de red al enviar a ${x.nombre}`, 'warning');
    }
    setEnviando(s => ({ ...s, [x.debt.id]: false }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MessageCircle size={18} color="#25D366" />
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>Recordatorios por WhatsApp</h2>
          </div>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--bg-input)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
            La IA redacta un mensaje personalizado para cada deudor con teléfono. Revisalo, editalo si querés, y enviá.
            {apiConfigured ? (
              <div style={{ marginTop: 6, color: '#25D366', fontWeight: 600 }}>✓ Envío automático activo (WhatsApp Business API). Las plantillas se envían directo; el texto libre de la IA sirve de referencia.</div>
            ) : (
              <div style={{ marginTop: 6 }}><strong>Modo manual:</strong> tocá "Abrir en WhatsApp" y confirmá cada mensaje. Para envío automático, configurá la WhatsApp Business API en Railway.</div>
            )}
            {sinTelefono > 0 && <div style={{ marginTop: 6, color: 'var(--amber)' }}>⚠ {sinTelefono} deudor{sinTelefono !== 1 ? 'es' : ''} sin teléfono cargado (no aparece{sinTelefono !== 1 ? 'n' : ''} en la lista).</div>}
          </div>

          {pendientes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text-muted)' }}>
              <Phone size={28} style={{ marginBottom: 10, opacity: 0.3 }} />
              <p>No hay deudores pendientes con teléfono para contactar.</p>
            </div>
          ) : !generado ? (
            <div style={{ textAlign: 'center', padding: '28px 20px' }}>
              <p style={{ fontSize: 13.5, marginBottom: 16 }}>Hay <strong>{pendientes.length}</strong> deudor{pendientes.length !== 1 ? 'es' : ''} pendiente{pendientes.length !== 1 ? 's' : ''} con teléfono.</p>
              <button className="btn btn-primary" onClick={generarMensajes} disabled={loading}>
                {loading ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Redactando mensajes...</> : <><Sparkles size={14} /> Redactar {pendientes.length} mensaje{pendientes.length !== 1 ? 's' : ''} con IA</>}
              </button>
              {error && <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 12 }}>{error}</div>}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 440, overflowY: 'auto' }}>
              {pendientes.map((x) => {
                const yaContactado = !!x.debt.whatsappSentAt;
                return (
                  <div key={x.debt.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', background: 'var(--bg-card)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 13.5 }}>{x.nombre}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>{x.phoneRaw}</span>
                        {x.isOverdue && <span className="badge badge-red" style={{ fontSize: 10, marginLeft: 8 }}>vencida</span>}
                        {yaContactado && <span className="badge badge-green" style={{ fontSize: 10, marginLeft: 6 }}>✓ contactado</span>}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--red)' }}>{x.debt.currency === 'USD' ? 'U$S' : '$'} {Number(x.debt.amount || 0).toLocaleString('es-UY')}</span>
                    </div>
                    <textarea
                      value={mensajes[x.debt.id] || ''}
                      onChange={e => setMensajes(m => ({ ...m, [x.debt.id]: e.target.value }))}
                      rows={4}
                      style={{ width: '100%', resize: 'vertical', fontSize: 12.5, lineHeight: 1.5 }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => abrirWhatsApp(x)}>
                        <Send size={13} /> Abrir en WhatsApp
                      </button>
                      {apiConfigured && (
                        <button
                          className="btn btn-sm"
                          style={{ background: '#25D366', color: '#fff', border: 'none', opacity: enviando[x.debt.id] ? 0.6 : 1 }}
                          disabled={enviando[x.debt.id]}
                          onClick={() => enviarApi(x)}
                        >
                          {enviando[x.debt.id]
                            ? <><Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> Enviando...</>
                            : enviadoApi[x.debt.id] === 'sent'
                              ? <><CheckCircle size={13} /> Enviado</>
                              : <><MessageCircle size={13} /> Enviar automático</>}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {error && generado && <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 8 }}>{error}</div>}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">Cerrar</button>
          {generado && (
            <button className="btn btn-secondary" onClick={() => { setGenerado(false); setMensajes({}); }}>
              <Sparkles size={13} /> Volver a redactar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DebtsPage() {
  const { debts, updateDebt, deleteDebt, clients, policies, cancelPolicy, showToast } = useApp();
  const navigate = useNavigate();
  const [modal, setModal] = useState(false);
  const [editDebt, setEditDebt] = useState(null);
  const [importModal, setImportModal] = useState(false);
  const [iaModal, setIaModal] = useState(false);
  const [whatsappModal, setWhatsappModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('pending');
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  // Enlace directo desde un email: /debts?id=XXX resalta y hace scroll a esa
  // deuda. No hay modal de detalle, así que la marcamos y llevamos a la vista.
  // Si la deuda no está en el filtro actual (p.ej. ya pagada), pasa a "todas".
  const [highlightId, setHighlightId] = useState(null);
  const rowRefs = useRef({});
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('id');
    if (!id) return;
    const deuda = debts.find(d => d.id === id);
    if (!deuda) return;
    if (deuda.status !== 'pending') setFilterStatus('all');
    setHighlightId(id);
    window.history.replaceState({}, '', window.location.pathname);
  }, [debts]);

  useEffect(() => {
    if (!highlightId) return;
    const el = rowRefs.current[highlightId];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => setHighlightId(null), 4000);
    return () => clearTimeout(t);
  }, [highlightId, filterStatus]);

  const getClient = id => clients.find(c=>c.id===id)||{};
  const getPolicy = id => policies.find(p=>p.id===id)||{};

  const filtered = debts
    .filter(d=>filterStatus==='all'||d.status===filterStatus)
    .sort((a,b)=>a.dueDate>b.dueDate?1:-1);

  const pending = debts.filter(d=>d.status==='pending');
  const overdue = pending.filter(d=>d.dueDate<todayStr);

  const markPaid = async (debt) => {
    await updateDebt(debt.id, { status:'paid', paidAt:new Date().toISOString() });
    showToast(`✅ ${getClient(debt.clientId).name || debt.clientName || 'Mora'} — mora regularizada`);
  };

  const markCancelled = async (debt) => {
    const client = getClient(debt.clientId);
    const nombre = client.name || debt.clientName || 'cliente';
    const policy = getPolicy(debt.policyId);
    if (!window.confirm(
      `¿Cancelar la póliza de ${nombre} por falta de pago?\n\n` +
      `Póliza: ${policy.number||'sin número'}\n\n` +
      `Esta acción marcará la póliza como CANCELADA.`
    )) return;
    await updateDebt(debt.id, { status:'cancelled', cancelledAt:new Date().toISOString() });
    if (debt.policyId) await cancelPolicy(debt.policyId, 'Falta de pago — mora no regularizada');
    showToast(`❌ Póliza de ${nombre} cancelada por falta de pago`, 'warning');
  };

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Deudores</h1>
          <p className="page-subtitle">
            {overdue.length>0 && <span style={{ color:'var(--red)', fontWeight:600 }}>⚠️ {overdue.length} vencidas · </span>}
            {pending.length} pendientes
          </p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" style={{ borderColor:'#25D366', color:'#25D366' }} onClick={()=>setWhatsappModal(true)}>
            <MessageCircle size={14} /> Recordar por WhatsApp
          </button>
          <button className="btn btn-secondary" onClick={()=>setIaModal(true)}>
            <Sparkles size={14} /> Pegar mail/texto
          </button>
          <button className="btn btn-secondary" onClick={()=>setImportModal(true)}>
            <Upload size={14} /> Importar archivo
          </button>
          <button className="btn btn-primary" onClick={()=>{setEditDebt(null);setModal(true);}}>
            <Plus size={15} /> Registrar mora
          </button>
        </div>
      </div>

      {overdue.length>0 && (
        <div style={{ background:'var(--red-dim)', border:'1px solid rgba(255,77,109,0.3)', borderRadius:10, padding:'12px 16px', marginBottom:20, fontSize:13, color:'var(--red)' }}>
          <strong>🔴 {overdue.length} mora{overdue.length>1?'s':''} vencida{overdue.length>1?'s':''}</strong> — Revisá si regularizaron o procedé a cancelar la póliza.
        </div>
      )}

      {/* Stats */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        {[
          { label:'Pendientes', value:pending.length, color:'var(--amber)', status:'pending' },
          { label:'Vencidas', value:overdue.length, color:'var(--red)', status:'pending' },
          { label:'Regularizadas', value:debts.filter(d=>d.status==='paid').length, color:'var(--accent)', status:'paid' },
          { label:'Canceladas', value:debts.filter(d=>d.status==='cancelled').length, color:'var(--text-muted)', status:'cancelled' },
        ].map(s=>(
          <div key={s.label} onClick={()=>setFilterStatus(s.status)} className="card"
            style={{ flex:1, minWidth:110, cursor:'pointer', padding:'12px 16px', borderLeft:`3px solid ${s.color}` }}>
            <div style={{ fontSize:11, color:s.color, fontFamily:'var(--font-display)', fontWeight:600 }}>{s.label}</div>
            <div style={{ fontSize:22, fontFamily:'var(--font-display)', fontWeight:700, color:s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        {[['pending','Pendientes'],['paid','Regularizadas'],['cancelled','Canceladas'],['all','Todas']].map(([v,l])=>(
          <button key={v} onClick={()=>setFilterStatus(v)} className="btn btn-sm" style={{
            background:filterStatus===v?'var(--accent-dim)':'transparent',
            color:filterStatus===v?'var(--accent)':'var(--text-muted)',
            border:`1px solid ${filterStatus===v?'var(--accent)':'var(--border)'}`,
          }}>{l}</button>
        ))}
      </div>

      {/* Tabla */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Cliente</th><th>Póliza</th><th>Compañía</th><th>Monto</th><th>Cuota</th><th>Vencimiento</th><th>Motivo</th><th>Estado</th><th>Acciones</th></tr>
            </thead>
            <tbody>
              {filtered.map(d=>{
                const client=getClient(d.clientId);
                const policy=getPolicy(d.policyId);
                const isOverdue=d.status==='pending'&&d.dueDate<todayStr;
                const daysOverdue=isOverdue?differenceInDays(today,parseISO(d.dueDate)):null;
                // Nombre a mostrar: cliente vinculado, o clientName, o el escondido en notas
                let nombreMostrar = client.name || d.clientName || '';
                if (!nombreMostrar && d.notes) {
                  const mm = d.notes.match(/(?:Cliente sin ficha|Asegurado):\s*([^·]+)/i);
                  if (mm) nombreMostrar = mm[1].trim();
                }
                return (
                  <tr key={d.id}
                    ref={el => { if (el) rowRefs.current[d.id] = el; }}
                    style={highlightId === d.id ? { background: 'rgba(124,108,248,0.16)', transition: 'background 0.4s' } : { transition: 'background 0.4s' }}>
                    <td>
                      <div style={{ fontWeight:500 }}>{nombreMostrar || '—'}{!client.name && nombreMostrar ? <span style={{ fontSize:10, color:'var(--text-muted)', fontWeight:400, marginLeft:6 }}>(sin ficha)</span> : null}</div>
                      <div style={{ fontSize:11, color:'var(--text-muted)' }}>{client.phone}</div>
                      {d.assignedTo ? <div style={{ fontSize:10, color:'var(--accent)', marginTop:2 }}>👤 {d.assignedTo}</div> : null}
                    </td>
                    <td style={{ fontFamily:'monospace', fontSize:12 }}>
                      {d.policyId && policy.id ? (
                        <span onClick={()=>navigate(`/policies/${policy.id}`)} style={{ color:'var(--accent)', cursor:'pointer', textDecoration:'underline' }}>{policy.number||'ver póliza'}</span>
                      ) : (policy.number||'—')}
                    </td>
                    <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{policy.company || companiaCanonica(d.company) || '—'}</td>
                    <td style={{ fontFamily:'var(--font-display)', fontWeight:700, color:'var(--red)' }}>
                      {d.currency==='USD'?'U$S':'$'} {Number(d.amount||0).toLocaleString('es-UY')}
                    </td>
                    <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{d.cuotas||'—'}</td>
                    <td style={{ fontSize:12 }}>
                      <span style={{ color:isOverdue?'var(--red)':'var(--text-secondary)' }}>
                        {d.dueDate?format(parseISO(d.dueDate),'dd/MM/yyyy'):'—'}
                      </span>
                      {isOverdue&&<div style={{ fontSize:10, color:'var(--red)', fontWeight:600 }}>+{daysOverdue}d vencida</div>}
                    </td>
                    <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{d.motivo||'—'}</td>
                    <td>
                      {d.status==='paid'&&<span className="badge badge-green">✅ Regularizada</span>}
                      {d.status==='cancelled'&&<span className="badge badge-red">❌ Cancelada</span>}
                      {d.status==='pending'&&(isOverdue
                        ?<span className="badge badge-red">⚠️ Vencida</span>
                        :<span className="badge badge-amber">Pendiente</span>
                      )}
                    </td>
                    <td>
                      {d.status==='pending' && (
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="btn btn-primary btn-sm" onClick={()=>markPaid(d)}>
                            <CheckCircle size={12} /> Pagó
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={()=>markCancelled(d)}>
                            <XCircle size={12} /> No pagó
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={()=>{setEditDebt(d);setModal(true);}}>✏️</button>
                          <button className="btn btn-secondary btn-sm" title="Eliminar deuda" onClick={()=>{if(window.confirm('¿Eliminar esta deuda? Esta acción no se puede deshacer.'))deleteDebt(d.id);}}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      )}
                      {d.status!=='pending' && (
                        <button className="btn btn-secondary btn-sm" onClick={()=>{if(window.confirm('¿Eliminar?'))deleteDebt(d.id);}}>
                          Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length===0 && (
          <div style={{ textAlign:'center', padding:'48px 20px', color:'var(--text-muted)' }}>
            <CheckCircle size={32} style={{ marginBottom:12, opacity:0.3 }} />
            <p>{filterStatus==='pending'?'No hay moras pendientes':'Sin registros'}</p>
            {filterStatus==='pending' && (
              <button className="btn btn-primary" style={{ marginTop:12 }} onClick={()=>setModal(true)}>
                Registrar primera mora
              </button>
            )}
          </div>
        )}
      </div>

      {modal && <DebtModal debt={editDebt} onClose={()=>{setModal(false);setEditDebt(null);}} />}
      {importModal && <ImportDebtorsModal onClose={()=>setImportModal(false)} />}
      {whatsappModal && <WhatsAppModal onClose={()=>setWhatsappModal(false)} />}
      {iaModal && <DebtIAModal onClose={()=>setIaModal(false)} onDone={(n)=>{ setIaModal(false); if(n>0) showToast(`✅ ${n} mora${n!==1?'s':''} registrada${n!==1?'s':''}`); }} />}
    </div>
  );
}